import React, { useEffect, useState } from 'react';

interface CarouselSlide {
  src: string;
  alt: string;
  title: string;
  description: string;
}

interface CarouselProps {
  slides: CarouselSlide[];
  autoPlayInterval?: number;
}

const Carousel: React.FC<CarouselProps> = ({ slides, autoPlayInterval = 5000 }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % slides.length);
    }, autoPlayInterval);

    return () => clearInterval(interval);
  }, [isAutoPlaying, autoPlayInterval, slides.length]);

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 100);
  };

  const goToPrevious = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + slides.length) % slides.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 100);
  };

  const goToNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % slides.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 100);
  };

  const currentSlide = slides[currentIndex];

  return (
    <div className="carousel-container">
      <div className="carousel-main">
        <img
          src={currentSlide.src}
          alt={currentSlide.alt}
          className="carousel-image"
          onClick={goToNext}
          loading="lazy"
        />
        <div className="carousel-content">
          <h3>{currentSlide.title}</h3>
          <p>{currentSlide.description}</p>
        </div>
      </div>

      <div className="carousel-controls">
        <button
          type="button"
          onClick={goToPrevious}
          className="carousel-button carousel-button-prev"
          aria-label="Previous slide"
        >
          ‹
        </button>
        <button
          type="button"
          onClick={goToNext}
          className="carousel-button carousel-button-next"
          aria-label="Next slide"
        >
          ›
        </button>
      </div>

      <div className="carousel-indicators">
        {slides.map((_, index) => (
          <button
            key={index}
            type="button"
            onClick={() => goToSlide(index)}
            className={`carousel-dot ${index === currentIndex ? 'carousel-dot-active' : ''}`}
            aria-label={`Go to slide ${index + 1}`}
            aria-current={index === currentIndex}
          />
        ))}
      </div>
    </div>
  );
};

export default Carousel;
